import React, { useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import api from '../../services/api';
import './CommunityImageFlush.css';

function toIsoStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function toIsoEnd(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function rangeReady(range) {
  return Array.isArray(range) && range[0] && range[1];
}

export default function CommunityImageFlush() {
  const toast = useRef(null);
  const [range, setRange] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [folder, setFolder] = useState('community_reports');
  const [truncated, setTruncated] = useState(false);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const fetchUrls = async () => {
    if (!rangeReady(range)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Date range',
        detail: 'Select a start and end date',
      });
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.get('/community_reports/flush-images', {
        params: {
          from: toIsoStart(range[0]),
          to: toIsoEnd(range[1]),
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setFolder(data.folder || 'community_reports');
      setTruncated(Boolean(data.truncated));
      setSelected([]);
      if (!(data.items || []).length) {
        toast.current?.show({
          severity: 'info',
          summary: 'No images',
          detail: `No photos in Cloudinary folder "${data.folder || 'community_reports'}" for that range`,
        });
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.detail || err?.response?.data?.error || 'Failed to load image URLs',
      });
    } finally {
      setLoading(false);
    }
  };

  const runFlush = async () => {
    if (!rangeReady(range) || !selected.length) return;
    try {
      setFlushing(true);
      const { data } = await api.post('/community_reports/flush-images', {
        from: toIsoStart(range[0]),
        to: toIsoEnd(range[1]),
        publicIds: selected.map((row) => row.publicId || row.id),
      });
      toast.current?.show({
        severity: data.failed ? 'warn' : 'success',
        summary: 'Flush finished',
        detail: `Deleted ${data.deleted} · failed ${data.failed} · skipped ${data.skipped}`,
      });
      const deletedIds = new Set(
        (data.results || [])
          .filter((r) => ['deleted', 'ok', 'not_found', 'not found'].includes(r.result))
          .map((r) => r.publicId || r.id)
      );
      setItems((prev) => prev.filter((row) => !deletedIds.has(row.publicId || row.id)));
      setTotal((prev) => Math.max(0, prev - deletedIds.size));
      setSelected([]);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.detail || err?.response?.data?.error || 'Flush failed',
      });
    } finally {
      setFlushing(false);
    }
  };

  const confirmFlush = () => {
    if (!selected.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Selection',
        detail: 'Select the rows you want to delete from Cloudinary',
      });
      return;
    }
    confirmDialog({
      header: 'Delete from Cloudinary',
      message: `Delete ${selected.length} selected image${selected.length === 1 ? '' : 's'} from folder "${folder}" and clear matching report URLs? This cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: runFlush,
    });
  };

  return (
    <Card>
      <div className="card p-4">
        <Toast ref={toast} />
        <ConfirmDialog />
        <div className="grid mb-4">
          <div className="col-6 flex align-items-center">
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>Flush report images</span>
              <Badge
                className="ml-2 b-4 relative"
                value={total ?? 0}
                style={{ bottom: '3px', backgroundColor: '#2020A0' }}
              />
            </h2>
          </div>
          <div className="col-6 flex justify-content-end">
            <Button
              label={`Flush selected (${selected.length})`}
              icon="pi pi-trash"
              className="p-button-danger p-button-sm"
              onClick={confirmFlush}
              loading={flushing}
              disabled={!selected.length || flushing}
            />
          </div>
        </div>

        <p className="admin-page-hint mb-4">
          Lists URLs from Cloudinary folder <code>{folder}</code> by upload date.
          New report photos go there; CMS images are not included.
        </p>

        <div className="grid mb-4">
          <div className="col-12 flex gap-2 align-items-end flex-wrap">
            <span style={{ minWidth: 260 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Date range</strong>
              </label>
              <Calendar
                value={range}
                onChange={(e) => setRange(e.value)}
                selectionMode="range"
                dateFormat="yy-mm-dd"
                placeholder="From — To"
                showIcon
                readOnlyInput
                hideOnRangeSelection
                className="w-full"
              />
            </span>
            <Button
              label="Load URLs"
              icon="pi pi-search"
              className="p-button-primary p-button-sm secondary-button"
              onClick={fetchUrls}
              loading={loading}
              disabled={!rangeReady(range)}
            />
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-text text-danger p-button-sm"
              onClick={() => {
                setRange(null);
                setItems([]);
                setTotal(0);
                setSelected([]);
                setTruncated(false);
              }}
            />
          </div>
        </div>

        {truncated && (
          <p className="flush-truncated">
            Showing the first {items.length} URLs in <code>{folder}</code>. Narrow the date range to cover the rest.
          </p>
        )}

        <DataTable
          value={items}
          selection={selected}
          onSelectionChange={(e) => setSelected(e.value)}
          dataKey="id"
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          loading={loading || flushing}
          emptyMessage={`Load a date range to list URLs from folder "${folder}"`}
          selectionMode="checkbox"
          className="p-datatable-sm"
          showGridlines
          stripedRows
        >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          <Column
            header="Uploaded"
            body={(row) => row.createdAt && new Date(row.createdAt).toLocaleString()}
            style={{ width: '12rem' }}
          />
          <Column
            header="Status"
            body={(row) => (row.status ? <Tag value={row.status} /> : '—')}
            style={{ width: '9rem' }}
          />
          <Column field="crossingName" header="Crossing" style={{ width: '12rem' }} />
          <Column
            header="Public ID"
            body={(row) => <span className="flush-public-id">{row.publicId}</span>}
            style={{ width: '16rem' }}
          />
          <Column
            header="Image URL"
            body={(row) => (
              <a
                className="flush-url"
                href={row.photoUrl}
                target="_blank"
                rel="noreferrer"
                title={row.photoUrl}
              >
                {row.photoUrl}
              </a>
            )}
          />
          <Column
            header="View"
            style={{ width: '6rem', textAlign: 'center' }}
            body={(row) => (
              <Button
                type="button"
                icon="pi pi-eye"
                className="p-button-text p-button-sm"
                onClick={() => setViewItem(row)}
                tooltip="View image"
                tooltipOptions={{ position: 'top' }}
              />
            )}
          />
        </DataTable>

        <p className="admin-page-hint mt-3 mb-0">
          {total ? `${total} URL${total === 1 ? '' : 's'} in ${folder}` : 'No URLs loaded'}
          {selected.length ? ` · ${selected.length} selected` : ''}
        </p>

        <Dialog
          header="Report image"
          visible={Boolean(viewItem)}
          onHide={() => setViewItem(null)}
          style={{ width: 'min(720px, 96vw)' }}
          breakpoints={{ '960px': '96vw' }}
        >
          {viewItem && (
            <div className="flush-modal">
              <img src={viewItem.photoUrl} alt="Report" />
              <a href={viewItem.photoUrl} target="_blank" rel="noreferrer">
                {viewItem.photoUrl}
              </a>
            </div>
          )}
        </Dialog>
      </div>
    </Card>
  );
}
